/**
 * ApprovalDrawer — slide-out editor for a pending draft-reply approval
 * (identity-request replies), in the InvoiceDrawer idiom: portal + scrim +
 * right-anchored panel, Escape/click-outside close, approve/reject footer.
 *
 * The recipient is read-only; subject and message are editable with an
 * "edited" badge when they differ from the skill's draft. Edits are built
 * through the shared buildDraftReplyEditedPayload helper (diff-only keys,
 * html regenerated from edited text) and posted via decideApproval to
 * POST /v1/approvals/{id}/decision — the same proven contract Mission
 * Control's ReviewSidebar uses. Defensive: a non-draft-reply payload renders
 * a read-only fallback (decide it in Mission Control) instead of crashing.
 *
 * All colours flow from theme/tokens.ts — no raw hex here.
 */
import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, Check } from 'lucide-react'
import { decideApproval } from './api'
import { ApiError } from '../../api/client'
import type { DashApproval } from './types'
import { asDraftReplyPayload, buildDraftReplyEditedPayload } from '../../features/review/draftReply'
import { accent, attention, danger, overlay } from '../../theme/tokens'

// Quiet amber tell that a field differs from the skill's draft.
function EditedBadge() {
  return (
    <span className="text-11 font-semibold uppercase tracking-overline px-1.5 rounded"
      style={{ background: attention['banner-bg'], border: `1px solid ${attention['banner-border']}`, color: attention['banner-text'] }}>
      edited
    </span>
  )
}

export default function ApprovalDrawer({ approval, onClose, onDecided, canDecide }: {
  approval: DashApproval
  onClose: () => void
  onDecided: () => void
  /** Session role ∈ {owner, admin} (from /v1/me). False hides the decide actions — fail closed. */
  canDecide: boolean
}) {
  const parsed = asDraftReplyPayload(approval.payload)

  const [replySubject, setReplySubject] = useState('')
  const [replyText, setReplyText] = useState('')
  const [busy, setBusy] = useState<'approved' | 'rejected' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Seed the editor from the payload whenever the drawer opens on a different
  // approval (mirrors ReviewSidebar's node-change reset).
  useEffect(() => {
    const p = asDraftReplyPayload(approval.payload)
    setReplySubject(p?.subject ?? '')
    setReplyText(p?.text ?? '')
    setBusy(null)
    setError(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval.id])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const subjectEdited = parsed !== null && replySubject !== parsed.subject
  const textEdited = parsed !== null && replyText !== parsed.text

  async function decide(status: 'approved' | 'rejected') {
    if (busy) return
    setBusy(status)
    setError(null)
    try {
      if (status === 'approved' && parsed) {
        const editedPayload = buildDraftReplyEditedPayload(parsed, { subject: replySubject, text: replyText })
        await decideApproval(approval.id, 'approved', editedPayload)
      } else {
        await decideApproval(approval.id, status)
      }
      onDecided()
      onClose()
    } catch (err) {
      const forbidden = err instanceof ApiError && err.status === 403
      setError(
        forbidden
          ? "You don't have permission to decide approvals. Ask your workspace owner for access."
          : status === 'approved'
            ? "Couldn't send the reply. Try again."
            : "Couldn't reject this approval. Try again.",
      )
      setBusy(null)
    }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ position: 'fixed', top: 0, right: 0, height: '100dvh', width: 'min(760px, 100vw)', overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}
        className="bg-surface-card">

        {/* Header — overline (kind), then the action title */}
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div className="min-w-0">
            <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted mb-2">
              {parsed ? 'Identity request · draft reply' : approval.skill}
            </div>
            <div className="text-detail-title font-semibold text-ink-primary truncate">
              {parsed ? 'Reply to sender' : 'Approval'}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        {parsed ? (
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
            {/* Recipient — read-only (the skill addressed the original sender) */}
            <div>
              <div className="text-11 font-semibold uppercase tracking-overline text-ink-muted mb-1">To</div>
              <div className="text-13 text-ink-body">{parsed.to.join(', ')}</div>
            </div>

            {/* Subject — editable */}
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-11 font-semibold uppercase tracking-overline text-ink-muted">Subject</span>
                {subjectEdited && <EditedBadge />}
              </div>
              <input value={replySubject} onChange={e => setReplySubject(e.target.value)}
                className="w-full h-9 px-3 text-13 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent" />
            </div>

            {/* Message — editable; html is regenerated from this text on approve */}
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-11 font-semibold uppercase tracking-overline text-ink-muted">Message</span>
                {textEdited && <EditedBadge />}
              </div>
              <textarea value={replyText} onChange={e => setReplyText(e.target.value)} rows={14}
                className="w-full flex-1 px-3 py-2 text-13 leading-relaxed bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-faint focus:outline-none focus:border-accent resize-y" />
            </div>
          </div>
        ) : (
          /* Defensive fallback — an approval whose payload isn't a draft reply.
             Read-only: decide it from Mission Control, which renders every type. */
          <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-3">
            <p className="text-13 text-ink-body leading-relaxed">{approval.summary}</p>
            <p className="text-12 text-ink-muted">This approval type can't be edited here.</p>
          </div>
        )}

        {/* Footer — decide actions (draft-reply only, role-gated) + the honest send consequence */}
        {parsed && (
          <div className="px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
            {canDecide ? (
              <>
                {error && <div className="text-12 font-medium mb-3" style={{ color: danger.text }}>{error}</div>}
                <div className="flex items-center gap-2">
                  <button onClick={() => decide('rejected')} disabled={busy !== null}
                    className="flex-1 h-10 text-13 font-semibold rounded-control border border-edge-control bg-surface-control text-ink-body transition-colors hover:bg-surface-nav-hover disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center">
                    {busy === 'rejected' ? 'Rejecting…' : 'Reject'}
                  </button>
                  <button onClick={() => decide('approved')} disabled={busy !== null}
                    className="flex-1 h-10 text-ink-on-accent text-13 font-semibold rounded-control transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    style={{ background: accent.DEFAULT }}>
                    <Check size={14} /> {busy === 'approved' ? 'Sending…' : 'Approve & send'}
                  </button>
                </div>
                <div className="text-11 text-ink-muted mt-2">Sends immediately from hr-assistant@klaxiora-mail.com on approve.</div>
              </>
            ) : (
              <div className="text-12 text-ink-muted">You don't have permission to decide approvals. Ask your workspace owner.</div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
